import { type Driver, TRANSPORT_NAME_MAX, VEHICLE_TYPES, type VehicleType, makeCreateDriverRequest, makePatchDriverRequest } from "@seagames/contracts";
import { Field, Modal, ModalActions, Select } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiError } from "../../lib/api/client";
import { clusterVehiclesOptions, createDriver, invalidateFleet, updateDriver } from "../../lib/api/transport-fleet";
import { toast } from "../../lib/ui/toast";
import { inputClass, refsOf, routeSaveError, useProblemText } from "./form-bits";
import { ClusterSelect, useCallingCodes, useProviderSelectOptions } from "./hooks";
import { useTransportFleetFormMessages as useFleetFormMessages } from "../../lib/i18n/use-form-schemas";
import { type DriverDraft, type DriverField, type Problems, checkDriver, driverBody, driverDraftFrom, driverPatch, emptyDriverDraft, hasProblems, issuesToProblems, keepFittingVehicles, splitE164 } from "./model";
import { ISO_WEEKDAYS, VehicleTypeIcon, useVehicleTypeLabel, useWeekdayName } from "./parts";

export type DriverFormTarget = { mode: "create" } | { mode: "edit"; driver: Driver };

const KNOWN: readonly DriverField[] = ["name", "number", "cluster_id", "provider_id", "vehicle_types", "vehicle_ids", "shift_start", "shift_end", "rest_isodow", "is_active"];

/**
 * Add or edit a driver (her `dvSheet`, l3.js:779): name, phone, cluster,
 * certified vehicle types, usual vehicles (the cluster's own, per ticked
 * type), shift and rest day — plus the company (provider logins, PLAN Q1).
 * Changing the cluster clears the usual vehicles (they belong to the old
 * one); unticking a type drops that type's vehicles. With trips booked, a
 * new cluster or a removed type → 409 with the refs, shown on the field.
 */
export function DriverFormModal({ target, onClose }: { target: DriverFormTarget | null; onClose: () => void }) {
	if (!target) return null;
	return target.mode === "create" ? <DriverForm onClose={onClose} /> : <DriverForm key={target.driver.driver_id} before={target.driver} onClose={onClose} />;
}

function DriverForm({ before, onClose }: { before?: Driver; onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const problemText = useProblemText();
	const typeLabel = useVehicleTypeLabel();
	const weekday = useWeekdayName();
	const callingCodes = useCallingCodes();
	const providers = useProviderSelectOptions();
	const messages = useFleetFormMessages();
	const [draft, setDraft] = useState<DriverDraft>(() => (before ? driverDraftFrom(before, callingCodes) : emptyDriverDraft()));
	const [problems, setProblems] = useState<Problems<DriverField>>({});
	const [busy, setBusy] = useState(false);
	const firstField = useRef<HTMLInputElement>(null);
	const clusterVehicles = useQuery({ ...clusterVehiclesOptions(draft.cluster_id), enabled: !!draft.cluster_id });

	// The calling codes may arrive after the sheet opens: split the stored number once they do.
	const split = useRef(callingCodes.length > 0);
	useEffect(() => {
		if (!before || split.current || callingCodes.length === 0) return;
		split.current = true;
		const [dial, number] = splitE164(before.phone, callingCodes);
		setDraft((d) => ({ ...d, dial, number }));
	}, [before, callingCodes]);

	// The vehicle types the sheet knows: the cluster's list, plus the driver's stored links.
	const typeOf = useMemo(() => {
		const m = new Map<string, VehicleType>();
		for (const v of before?.usual_vehicles ?? []) m.set(v.vehicle_id, v.vehicle_type);
		for (const v of clusterVehicles.data?.items ?? []) m.set(v.vehicle_id, v.vehicle_type);
		return (id: string) => m.get(id);
	}, [before, clusterVehicles.data]);

	const set = <K extends DriverField>(k: K, v: DriverDraft[K]) => {
		setDraft((d) => ({ ...d, [k]: v }));
		setProblems((p) => (p[k] ? { ...p, [k]: undefined } : p));
	};

	const setCluster = (clusterId: string) => {
		if (clusterId === draft.cluster_id) return;
		if (draft.vehicle_ids.length > 0) toast.info(t("driverForm.vehiclesCleared"));
		setDraft((d) => ({ ...d, cluster_id: clusterId, vehicle_ids: [] }));
		setProblems((p) => ({ ...p, cluster_id: undefined, vehicle_ids: undefined }));
	};

	const toggleType = (type: VehicleType, on: boolean) => {
		const types = VEHICLE_TYPES.filter((x) => (x === type ? on : draft.vehicle_types.includes(x)));
		setDraft((d) => ({ ...d, vehicle_types: types, vehicle_ids: keepFittingVehicles(d.vehicle_ids, types, typeOf) }));
		setProblems((p) => ({ ...p, vehicle_types: undefined, vehicle_ids: undefined }));
	};

	const toggleVehicle = (id: string, on: boolean) => set("vehicle_ids", on ? [...draft.vehicle_ids, id] : draft.vehicle_ids.filter((x) => x !== id));

	const special = (err: ApiError): Problems<DriverField> | null => {
		if (err.code !== "CONFLICT") return null;
		const refs = refsOf(err);
		if (refs.length === 0) return null;
		const field = err.details?.["field"] === "vehicle_types" ? "vehicle_types" : "cluster_id";
		return { [field]: { key: "upcomingTrips", values: { count: refs.length, refs: refs.slice(0, 3).join(", ") } } };
	};

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		const found = checkDriver(draft, typeOf);
		setProblems(found);
		if (hasProblems(found)) return;
		setBusy(true);
		try {
			if (!before) {
				const parsed = makeCreateDriverRequest(messages).safeParse(driverBody(draft));
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, KNOWN, "name", { phone: "number" }));
					return;
				}
				const created = await createDriver(parsed.data);
				toast.success(t("driverForm.toast.added", { name: created.name }));
			} else {
				const body = driverPatch(draft, before, callingCodes);
				if (Object.keys(body).length === 0) {
					onClose();
					return;
				}
				const changed = Object.keys(body).map((k) => k.replace(/_ids?$/, "").replace(/_/g, " "));
				const parsed = makePatchDriverRequest(messages).safeParse({ ...body, version: before.version, reason: `Changed ${changed.join(", ")} for driver ${before.name}` });
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, KNOWN, "name", { phone: "number" }));
					return;
				}
				await updateDriver(before.driver_id, parsed.data);
				toast.success(t("driverForm.toast.updated", { name: driverBody(draft).name }));
			}
			await invalidateFleet();
			onClose();
		} catch (err) {
			routeSaveError(err, KNOWN, setProblems, before ? t("driverForm.toast.updateFailed") : t("driverForm.toast.addFailed"), special);
		} finally {
			setBusy(false);
		}
	};

	const idp = before ? "dv-edit" : "dv-add";
	const formId = `${idp}-form`;
	const dialOptions = callingCodes.map((c) => ({ value: c, label: `+${c}` }));
	const vehicles = clusterVehicles.data?.items ?? [];

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={before ? t("driverForm.editTitle") : t("driverForm.addTitle")}
			description={before ? before.name : t("driverForm.addDescription")}
			dismissible={!busy}
			initialFocusRef={firstField}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
						{tc("action.cancel")}
					</button>
					<button type="submit" form={formId} className="btn btn-primary btn-sm" disabled={busy}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("driverForm.save")}
					</button>
				</ModalActions>
			}
		>
			<form id={formId} onSubmit={(e) => void submit(e)} noValidate className="grid gap-[0.75rem] sm:grid-cols-2">
				<Field label={t("driverForm.name")} htmlFor={`${idp}-name`} required error={problemText(problems.name)} className="sm:col-span-2">
					<input
						ref={firstField}
						id={`${idp}-name`}
						value={draft.name}
						onChange={(e) => set("name", e.target.value.toUpperCase())}
						autoComplete="off"
						maxLength={TRANSPORT_NAME_MAX}
						className={inputClass(!!problems.name, "uppercase")}
					/>
				</Field>

				<Field label={t("driverForm.phone")} htmlFor={`${idp}-phone`} required error={problemText(problems.number)} className="sm:col-span-2">
					<div className="grid grid-cols-[6.75rem_minmax(0,1fr)] gap-[0.5rem]">
						<Select value={draft.dial || null} onChange={(v) => set("dial", v ?? "")} options={dialOptions} placeholder="+" aria-label={t("driverForm.dial")} />
						<input
							id={`${idp}-phone`}
							type="tel"
							inputMode="tel"
							autoComplete="off"
							value={draft.number}
							onChange={(e) => set("number", e.target.value.replace(/[^\d\s-]/g, ""))}
							placeholder="123456789"
							className={inputClass(!!problems.number, "tabular-nums")}
						/>
					</div>
				</Field>

				<Field label={t("driverForm.cluster")} htmlFor={`${idp}-cluster`} required error={problemText(problems.cluster_id)} hint={t("driverForm.clusterHint")}>
					<ClusterSelect id={`${idp}-cluster`} value={draft.cluster_id} onChange={setCluster} current={before?.cluster} />
				</Field>
				<Field label={t("driverForm.company")} htmlFor={`${idp}-company`} aside={tc("state.optional")} error={problemText(problems.provider_id)}>
					<Select
						id={`${idp}-company`}
						value={draft.provider_id || null}
						onChange={(v) => set("provider_id", v ?? "")}
						options={
							before?.provider && !providers.options.some((o) => o.value === before.provider?.transport_provider_id)
								? [{ value: before.provider.transport_provider_id, label: before.provider.name }, ...providers.options]
								: providers.options
						}
						placeholder={t("vehicleForm.companyNone")}
						clearable
					/>
				</Field>

				<Field label={t("driverForm.certified")} htmlFor={`${idp}-types`} required error={problemText(problems.vehicle_types)} className="sm:col-span-2">
					<div id={`${idp}-types`} role="group" aria-label={t("driverForm.certified")} className="flex flex-wrap gap-x-[1.25rem] gap-y-[0.5rem]">
						{VEHICLE_TYPES.map((type) => (
							<label key={type} className="inline-flex cursor-pointer items-center gap-[0.5rem] text-[0.84375rem] text-gms-navy-ink">
								<input type="checkbox" className="checkbox checkbox-sm checkbox-primary" checked={draft.vehicle_types.includes(type)} onChange={(e) => toggleType(type, e.target.checked)} />
								<VehicleTypeIcon type={type} className="size-[1rem] text-gms-ink-3" />
								{typeLabel(type)}
							</label>
						))}
					</div>
				</Field>

				<Field label={t("driverForm.vehicles")} htmlFor={`${idp}-vehicles`} aside={tc("state.optional")} error={problemText(problems.vehicle_ids)} hint={t("driverForm.vehiclesHint")} className="sm:col-span-2">
					<div id={`${idp}-vehicles`} className="grid gap-[0.5rem]">
						{!draft.cluster_id ? (
							<p className="flex items-center gap-[0.375rem] text-[0.8125rem] text-gms-ink-3">
								<LockGlyph />
								{t("driverForm.vehiclesNeedCluster")}
							</p>
						) : draft.vehicle_types.length === 0 ? (
							<p className="text-[0.8125rem] text-gms-ink-3">{t("driverForm.vehiclesNeedType")}</p>
						) : clusterVehicles.isPending ? (
							<p className="text-[0.8125rem] text-gms-ink-3">{tc("state.loading")}</p>
						) : (
							draft.vehicle_types.map((type) => {
								const list = vehicles.filter((v) => v.vehicle_type === type && v.status !== "RETIRED");
								return (
									<fieldset key={type} className="rounded-field border border-gms-line bg-gms-soft px-[0.75rem] py-[0.5rem]">
										<legend className="px-[0.25rem] text-[0.75rem] font-semibold text-gms-ink-2">{t(`vehicleTypePlural.${type}`)}</legend>
										{list.length === 0 ? (
											<p className="text-[0.8125rem] text-gms-ink-3">{t("driverForm.noneInCluster")}</p>
										) : (
											<ul className="grid max-h-[11rem] gap-[0.25rem] overflow-y-auto">
												{list.map((v) => (
													<li key={v.vehicle_id}>
														<label className="flex cursor-pointer items-center gap-[0.5rem] text-[0.8125rem] text-gms-navy-ink">
															<input
																type="checkbox"
																className="checkbox checkbox-xs checkbox-primary"
																checked={draft.vehicle_ids.includes(v.vehicle_id)}
																onChange={(e) => toggleVehicle(v.vehicle_id, e.target.checked)}
															/>
															<span className="font-semibold tracking-[0.02em]">{v.plate}</span>
															<span className="truncate text-gms-ink-3">{t("driverForm.vehicleLine", { model: v.model, seats: v.seats })}</span>
														</label>
													</li>
												))}
											</ul>
										)}
									</fieldset>
								);
							})
						)}
					</div>
				</Field>

				<Field label={t("driverForm.shiftStart")} htmlFor={`${idp}-from`} required error={problemText(problems.shift_start)}>
					<input id={`${idp}-from`} type="time" value={draft.shift_start} onChange={(e) => set("shift_start", e.target.value)} className={inputClass(!!problems.shift_start, "tabular-nums")} />
				</Field>
				<Field label={t("driverForm.shiftEnd")} htmlFor={`${idp}-to`} required error={problemText(problems.shift_end)} hint={t("driverForm.shiftHint")}>
					<input id={`${idp}-to`} type="time" value={draft.shift_end} onChange={(e) => set("shift_end", e.target.value)} className={inputClass(!!problems.shift_end, "tabular-nums")} />
				</Field>

				<Field label={t("driverForm.restDay")} htmlFor={`${idp}-rest`} error={problemText(problems.rest_isodow)}>
					<Select
						id={`${idp}-rest`}
						value={draft.rest_isodow}
						onChange={(v) => set("rest_isodow", v ?? "")}
						options={[{ value: "", label: t("shift.noRest") }, ...ISO_WEEKDAYS.map((d) => ({ value: String(d), label: weekday(d) }))]}
						searchable={false}
					/>
				</Field>
				{before && (
					<label className="flex cursor-pointer items-center gap-[0.625rem] self-end pb-[0.375rem] text-[0.84375rem] text-gms-navy-ink">
						<input type="checkbox" className="toggle toggle-sm toggle-primary" checked={draft.is_active} onChange={(e) => set("is_active", e.target.checked)} />
						<span>
							<span className="font-medium">{t("driverForm.active")}</span>
							<span className="block text-[0.75rem] text-gms-ink-3">{t("driverForm.activeHint")}</span>
						</span>
					</label>
				)}
			</form>
		</Modal>
	);
}

function LockGlyph() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="size-[0.875rem] shrink-0">
			<rect x="5.5" y="10.5" width="13" height="9.5" rx="2" />
			<path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
		</svg>
	);
}
